/**
 * Labor Rate section for PM Service Form.
 * Each rate category records straight-time and overtime hours.
 * "+ add additional labor rate" adds another category row.
 */

import { useEffect, useMemo, useState } from 'react';
import { useUserContext } from '../../contexts/UserContext';
import { fetchLaborCategoriesByCompany, getCurrentLaborRate } from '../../graphql/pmQueries';
import type { LaborCategory, LaborRate } from '../../types/pmTypes';
import { Btn, InputField, SearchSuggest } from '../ui';

export interface LaborRateEntry {
    categoryId: string;
    categoryName: string;
    standardRate: number;
    overtimeRate: number;
    hoursST: string;
    hoursOT: string;
}

export interface LaborRateData {
    laborRateId: string;
    laborRateName: string;
    laborHoursST: string;
    laborHoursOT: string;
    entries?: LaborRateEntry[];
}

interface LaborRateSectionProps {
    laborCategoryId: string | null;
    data: LaborRateData;
    onChange: (data: LaborRateData) => void;
}

function parseHours(value: string): number {
    return parseFloat(value) || 0;
}

export const LaborRateSection = ({
    laborCategoryId,
    data,
    onChange,
}: LaborRateSectionProps) => {
    const { companyUser, companyOwner } = useUserContext();
    const companyId = (companyUser?.companyId as string) || companyOwner?.companyId || '';

    const [categories, setCategories] = useState<LaborCategory[]>([]);
    const [rateMap, setRateMap] = useState<Map<string, LaborRate>>(new Map());
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!companyId) return;
        let isCancelled = false;
        (async () => {
            try {
                const cats = (await fetchLaborCategoriesByCompany(companyId)) as LaborCategory[];
                if (isCancelled) return;
                const active = cats.filter((category) => category.isActive);
                setCategories(active);

                const map = new Map<string, LaborRate>();
                await Promise.all(
                    active.map(async (cat) => {
                        try {
                            const rate = await getCurrentLaborRate(cat.id);
                            if (rate) map.set(cat.id, rate as LaborRate);
                        } catch {
                            /* skip a category with no current rate */
                        }
                    }),
                );
                if (isCancelled) return;
                setRateMap(map);
            } catch (err) {
                if (!isCancelled) console.error('Failed to fetch labor categories:', err);
            } finally {
                if (!isCancelled) setLoading(false);
            }
        })();
        return () => {
            isCancelled = true;
        };
    }, [companyId]);

    const entries = data.entries || [];

    const totalHours = useMemo(
        () => entries.reduce((sum, entry) => sum + parseHours(entry.hoursST) + parseHours(entry.hoursOT), 0),
        [entries],
    );

    const categoryOptions = useMemo(
        () =>
            categories.map((cat) => {
                const rate = rateMap.get(cat.id);
                const rateLabel = rate
                    ? ` (ST $${rate.standardRate}/hr · OT $${rate.overtimeRate}/hr)`
                    : '';
                return { value: cat.id, label: `${cat.name}${rateLabel}` };
            }),
        [categories, rateMap],
    );

    const updateEntries = (newEntries: LaborRateEntry[]) => {
        const laborHoursST = newEntries.reduce((sum, entry) => sum + parseHours(entry.hoursST), 0);
        const laborHoursOT = newEntries.reduce((sum, entry) => sum + parseHours(entry.hoursOT), 0);
        const primaryEntry = newEntries.find((entry) => entry.categoryId);
        onChange({
            laborRateId: primaryEntry?.categoryId || '',
            laborRateName: primaryEntry?.categoryName || '',
            laborHoursST: laborHoursST > 0 ? String(laborHoursST) : '',
            laborHoursOT: laborHoursOT > 0 ? String(laborHoursOT) : '',
            entries: newEntries,
        });
    };

    useEffect(() => {
        if (loading || entries.length > 0) return;
        const assignedRate = laborCategoryId ? rateMap.get(laborCategoryId) : undefined;
        const assignedCategory = categories.find((category) => category.id === laborCategoryId);
        if (assignedRate && assignedCategory) {
            updateEntries([
                {
                    categoryId: assignedCategory.id,
                    categoryName: assignedCategory.name,
                    standardRate: assignedRate.standardRate,
                    overtimeRate: assignedRate.overtimeRate,
                    hoursST: '',
                    hoursOT: '',
                },
            ]);
            return;
        }
        updateEntries([
            {
                categoryId: '',
                categoryName: '',
                standardRate: 0,
                overtimeRate: 0,
                hoursST: '',
                hoursOT: '',
            },
        ]);
        // Seed a single row once rates have loaded.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, laborCategoryId, rateMap, categories]);

    const addEntry = () => {
        updateEntries([
            ...entries,
            {
                categoryId: '',
                categoryName: '',
                standardRate: 0,
                overtimeRate: 0,
                hoursST: '',
                hoursOT: '',
            },
        ]);
    };

    const removeEntry = (index: number) => {
        updateEntries(entries.filter((_, entryIndex) => entryIndex !== index));
    };

    const selectCategory = (index: number, catId: string) => {
        const updated = [...entries];
        const cat = categories.find((category) => category.id === catId);
        const rate = rateMap.get(catId);
        updated[index] = {
            ...updated[index],
            categoryId: catId,
            categoryName: cat?.name || '',
            standardRate: rate?.standardRate || 0,
            overtimeRate: rate?.overtimeRate || 0,
        };
        updateEntries(updated);
    };

    const updateHours = (index: number, field: 'hoursST' | 'hoursOT', hours: string) => {
        const updated = [...entries];
        updated[index] = { ...updated[index], [field]: hours };
        updateEntries(updated);
    };

    if (loading) {
        return (
            <div className="pm-inspection-section">
                <h3 className="pm-inspection-section-title">Labor Rate</h3>
                <p className="pm-labor-hint">Loading labor rates...</p>
            </div>
        );
    }

    if (categories.length === 0) {
        return (
            <div className="pm-inspection-section">
                <h3 className="pm-inspection-section-title">Labor Rate</h3>
                <p className="pm-labor-hint">
                    No labor rate categories configured. Set up labor rates in Company Resources.
                </p>
            </div>
        );
    }

    return (
        <div className="pm-inspection-section">
            <h3 className="pm-inspection-section-title">Labor Rate</h3>

            <div className="pm-labor-section">
                <div className="pm-labor-total">
                    <span className="pm-labor-total-label">Total Hours</span>
                    <span className="pm-labor-total-value">{totalHours > 0 ? totalHours : '0'}</span>
                    <span className="pm-labor-hint">hours</span>
                </div>

                {entries.map((entry, idx) => (
                    <div key={idx} className="pm-labor-row">
                        <div className="pm-labor-rate">
                            <label className="pm-inspection-label" htmlFor={`labor-rate-category-${idx}`}>
                                Labor Rate
                            </label>
                            <SearchSuggest
                                options={categoryOptions}
                                value={entry.categoryId}
                                onChange={(categoryId) => selectCategory(idx, categoryId)}
                                placeholder="Search labor rates..."
                                allowNew={false}
                                inputId={`labor-rate-category-${idx}`}
                                ariaLabel="Labor rate category"
                            />
                        </div>
                        <div className="pm-labor-hours">
                            <InputField
                                inputId={`labor-st-hours-${idx}`}
                                label="ST Hours"
                                type="number"
                                value={entry.hoursST}
                                onChange={(hours) => updateHours(idx, 'hoursST', hours)}
                                min="0"
                                step="0.25"
                                placeholder="0"
                                ariaLabel="Straight time hours"
                            />
                        </div>
                        <div className="pm-labor-hours">
                            <InputField
                                inputId={`labor-ot-hours-${idx}`}
                                label="OT Hours"
                                type="number"
                                value={entry.hoursOT}
                                onChange={(hours) => updateHours(idx, 'hoursOT', hours)}
                                min="0"
                                step="0.25"
                                placeholder="0"
                                ariaLabel="Overtime hours"
                            />
                        </div>
                        <Btn
                            variant="link"
                            buttonSize="sm"
                            buttonClasses="pm-labor-remove"
                            buttonText="Remove"
                            ariaLabel="Remove labor rate"
                            buttonDisabled={entries.length <= 1}
                            buttonOnClick={() => removeEntry(idx)}
                        />
                    </div>
                ))}

                <Btn
                    variant="link"
                    buttonSize="sm"
                    buttonText="+ add additional labor rate"
                    buttonOnClick={addEntry}
                />
            </div>
        </div>
    );
};
